import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import PageLayout from '../../components/PageLayout';
import SectionBlock from '../../components/SectionBlock';
import { Box, Typography, List, ListItem, ListItemText, Grid, Card, CardContent, Divider, Button, Drawer, Snackbar, Alert } from '@mui/material';
import { RequestQuote, ArrowBack } from '@mui/icons-material';
import QuoteQuestionnaireAdapter from '../../components/questionnaire/QuoteQuestionnaireAdapter';
import { QuoteProvider } from '../../contexts/QuoteContext';
import { QuestionnaireProvider } from '../../contexts/QuestionnaireContext';
import { trackButtonClick } from '../../utils/analytics';
import { useServiceCatalog } from '../../contexts/ServiceCatalogContext';
import Seo from '../../components/Seo';
// Remove direct import
// import serviceCatalog from '../../../full_service_catalog_all_services.json';



const checklistIdToEmoji = {
  'chk_deep_clean': '🧹',
  'chk_move_out': '🚚',
  'chk_vacation_turnover': '🏡',
  'chk_standard': '🧼',
  'chk_laundry': '🧺',
};

const ServiceDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { serviceCatalog, loading, error: catalogError, findServiceById } = useServiceCatalog();
  const [error, setError] = useState(null);
  const [questionnaireDrawerOpen, setQuestionnaireDrawerOpen] = useState(false);
  const [contactSuccess, setContactSuccess] = useState(false);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Service catalog data now managed by ServiceCatalogContext

  // Handler for questionnaire
  const handleQuestionnaireOpen = (buttonLocation) => {
    trackButtonClick('get_quote_questionnaire', buttonLocation);
    setQuestionnaireDrawerOpen(true);
  };

  const handleQuestionnaireComplete = (customerInfo, quoteInfo, rawAnswers) => {
    console.log('Quote questionnaire completed:', { customerInfo, quoteInfo, rawAnswers });
    setQuestionnaireDrawerOpen(false);
    setContactSuccess(true);
  };

  const handleQuestionnaireClose = () => {
    setQuestionnaireDrawerOpen(false);
  };

  // Smart back button logic
  const getBackButtonProps = () => {
    const referrer = document.referrer;
    const currentDomain = window.location.origin;
    
    // Check if user came from within our site
    if (referrer && referrer.startsWith(currentDomain)) {
      const referrerPath = new URL(referrer).pathname;
      
      // If they came from home page (especially services section)
      if (referrerPath === '/' || referrerPath.includes('#services')) {
        return {
          text: 'Back to Our Services',
          action: () => navigate('/#services')
        };
      }
      
      // If they came from another service page
      if (referrerPath.startsWith('/services/')) {
        return {
          text: 'Back to Services',
          action: () => navigate('/#services')
        };
      }
      
      // If they came from elsewhere on our site
      return {
        text: 'Back',
        action: () => window.history.back()
      };
    }
    
    // Default fallback - go to services section
    return {
      text: 'Back to Our Services',
      action: () => navigate('/#services')
    };
  };

  const handleSmartBack = () => {
    const backProps = getBackButtonProps();
    trackButtonClick('smart_back_button', `service_detail_${slug}`);
    backProps.action();
  };

  if (loading) {
    return (
      <PageLayout>
        <SectionBlock title="Loading..." bgcolor="background.default" py={{ xs: 4, md: 8 }} maxWidth="md">
          <Typography variant="h5" align="center" sx={{ fontSize: { xs: '1.125rem', sm: '1.25rem' }, color: 'text.secondary', minHeight: 120, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            Loading service details...
          </Typography>
        </SectionBlock>
      </PageLayout>
    );
  }

  if (catalogError || error || !serviceCatalog) {
    return (
      <PageLayout>
        <SectionBlock title="Error" bgcolor="background.paper" py={{ xs: 4, md: 8 }} maxWidth="md">
          <Typography color="error.main" variant="h4" align="center" sx={{ fontWeight: 700 }}>
            {catalogError || error || 'Failed to load service catalog.'}
          </Typography>
        </SectionBlock>
      </PageLayout>
    );
  }

  const service = serviceCatalog.services.find(s => s.id === slug);

  if (!service) {
    return (
      <PageLayout>
        <SectionBlock title="Service Not Found" bgcolor="background.paper" py={{ xs: 4, md: 8 }} maxWidth="md">
          <Typography color="error.main" variant="h4" align="center" sx={{ fontWeight: 700 }}>
            Sorry, we couldn't find this service.
          </Typography>
        </SectionBlock>
      </PageLayout>
    );
  }

  // Checklist
  const checklist = serviceCatalog.checklists.find(c => c.id === service.checklist_id);
  // Add-ons
  const addOns = (service.available_add_ons || []).map(id => serviceCatalog.add_ons_catalog.find(a => a.id === id)).filter(Boolean);

  return (
    <PageLayout>
      <Seo
        title={`${service.service_name || service.name} in Orlando & Central Florida`}
        description={
          service.description
            ? String(service.description).slice(0, 155)
            : `Professional ${(service.service_name || service.name || 'cleaning').toLowerCase()} by 5 Fairies Cleaning Services across Orlando, Kissimmee & Davenport.`
        }
        path={`/services/${slug}`}
      />
      {/* Smart Back Button */}
      <Box sx={{ 
        bgcolor: 'grey.50', 
        py: 2, 
        borderBottom: '1px solid',
        borderColor: 'divider'
      }}>
        <Box sx={{ 
          maxWidth: 'lg', 
          mx: 'auto', 
          px: { xs: 2, md: 3 }
        }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={handleSmartBack}
            sx={{
              color: 'primary.main',
              textTransform: 'none',
              fontSize: '0.95rem',
              fontWeight: 500,
              p: 1,
              borderRadius: 2,
              '&:hover': {
                bgcolor: 'rgba(0, 0, 0, 0.04)',
                color: 'primary.dark'
              }
            }}
          >
            {getBackButtonProps().text}
          </Button>
        </Box>
      </Box>

      <SectionBlock
        title={service.service_name}
        bgcolor="background.default"
        maxWidth="lg"
        py={{ xs: 4, md: 8 }}
      >
        {/* Service Tagline */}
        {service.tagline && (
          <Typography variant="h6" sx={{ color: 'primary.main', mb: 2, fontSize: { xs: '1rem', sm: '1.125rem' }, textAlign: 'center', fontWeight: 600, fontStyle: 'italic' }}>
            {service.tagline}
          </Typography>
        )}
        
        {/* Service Description */}
        <Typography variant="h5" sx={{ color: 'text.secondary', mb: 4, fontSize: { xs: '1.125rem', sm: '1.25rem' }, textAlign: 'center', fontWeight: 500, lineHeight: 1.6 }}>
          {service.description}
        </Typography>

        {/* Get Quote CTA Button */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 6 }}>
          <Button
            variant="contained"
            size="large"
            onClick={() => handleQuestionnaireOpen(`service_detail_${service.id}`)}
            startIcon={<RequestQuote />}
            sx={{
              bgcolor: 'primary.main',
              color: 'white',
              px: 4,
              py: 1.5,
              fontSize: { xs: '1rem', sm: '1.125rem' },
              fontWeight: 600,
              borderRadius: 3,
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              background: 'linear-gradient(135deg, #000000 0%, #FFD700 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #1a1a1a 0%, #FFA500 100%)',
                transform: 'translateY(-2px)',
                boxShadow: '0 6px 16px rgba(0,0,0,0.2)',
              },
              transition: 'all 0.3s ease'
            }}
          >
            Get Free Quote
          </Button>
        </Box>

        {/* Detailed Checklist Display */}
        {checklist && (
          <Box sx={{ mb: 4 }}>
            <Typography variant="h4" sx={{ fontWeight: 700, color: 'primary.main', mb: 3, fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' }, textAlign: 'center' }}>
              {checklistIdToEmoji[checklist.id] || '✅'} {checklist.name}
            </Typography>
            
            {/* Display detailed checklist groups and items */}
            {checklist.groups && checklist.groups.length > 0 && (
              <Grid container spacing={3}>
                {checklist.groups.map((group) => (
                  <Grid item xs={12} md={6} key={group.id}>
                    <Card sx={{ height: '100%', border: '1px solid', borderColor: 'divider' }}>
                      <CardContent>
                        <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main', mb: 2, fontSize: { xs: '1.125rem', sm: '1.25rem' } }}>
                          {group.title}
                        </Typography>
                        <List dense>
                          {group.items.map((item, index) => (
                            <ListItem key={index} sx={{ py: 0.5, px: 0 }}>
                              <ListItemText 
                                primary={item}
                                sx={{ 
                                  '& .MuiListItemText-primary': { 
                                    fontSize: { xs: '0.875rem', sm: '1rem' },
                                    lineHeight: 1.4,
                                    color: 'text.secondary'
                                  }
                                }}
                              />
                            </ListItem>
                          ))}
                        </List>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>
        )}

        {/* Enhanced Add-ons Display */}
        {addOns.length > 0 && (
          <Box sx={{ mt: 4 }}>
            <Divider sx={{ mb: 3 }} />
            <Typography variant="h5" sx={{ fontWeight: 600, color: 'primary.main', mb: 3, textAlign: 'center', fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
              Available Add-On Services
            </Typography>
            <Grid container spacing={2} justifyContent="center">
              {addOns.map(addon => (
                <Grid item key={addon.id} xs={12} sm={6} md={4}>
                  <Card sx={{ textAlign: 'center', border: '1px solid', borderColor: 'primary.light', '&:hover': { boxShadow: 3 } }}>
                    <CardContent sx={{ py: 2 }}>
                      <Typography variant="body1" sx={{ fontWeight: 500, color: 'text.primary', fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                        {addon.name}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}

        {/* Pricing Display - Ready for future integration */}
        {service.pricing && (
          <Box sx={{ mt: 4 }}>
            <Divider sx={{ mb: 3 }} />
            <Typography variant="h5" sx={{ fontWeight: 600, color: 'primary.main', mb: 3, textAlign: 'center', fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
              Pricing Information
            </Typography>
            <Card sx={{ textAlign: 'center', border: '1px solid', borderColor: 'primary.light' }}>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                  Starting at ${service.pricing.base_price}
                </Typography>
                {service.pricing.notes && (
                  <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
                    {service.pricing.notes}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Box>
        )}
      </SectionBlock>

      {/* Quote Questionnaire Drawer */}
      <Drawer
        anchor="right"
        open={questionnaireDrawerOpen}
        onClose={handleQuestionnaireClose}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: '90%', md: '70%', lg: '60%' },
            maxWidth: 800,
            bgcolor: 'background.paper'
          }
        }}
      >
        <QuoteProvider>
          <QuestionnaireProvider>
            <QuoteQuestionnaireAdapter
              onComplete={handleQuestionnaireComplete}
              onClose={handleQuestionnaireClose}
              configPath="/data/customer_engagement_questionnaire.json"
              serviceContext={{
                serviceId: service?.id,
                serviceName: service?.service_name,
                serviceType: service?.service_name
              }}
            />
          </QuestionnaireProvider>
        </QuoteProvider>
      </Drawer>

      {/* Success Notification */}
      <Snackbar
        open={contactSuccess}
        autoHideDuration={5000}
        onClose={() => setContactSuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setContactSuccess(false)} 
          severity="success" 
          sx={{ 
            width: '100%',
            background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
            color: 'white',
            '& .MuiAlert-icon': {
              color: 'white'
            }
          }}
        >
          Quote request sent successfully! We'll contact you soon with your personalized estimate.
        </Alert>
      </Snackbar>
    </PageLayout>
  );
};

// Wrap with context providers for quote functionality
const ServiceDetailWithProviders = () => {
  return (
    <QuoteProvider>
      <QuestionnaireProvider>
        <ServiceDetail />
      </QuestionnaireProvider>
    </QuoteProvider>
  );
};

export default ServiceDetailWithProviders; 